import { useEffect } from 'react'
import { AlertOctagon, PauseCircle, WifiOff, X } from 'lucide-react'
import { clock } from '@/lib/format'
import { useLive, worstTopic } from '@/stores/live'
import { useOverview } from './useOverview'
import { Button } from '@/components/shadcn/button'

/** Global banners under the top bar (03 §5.4): usage limit, server down, live connection lost. */
export function Banners() {
  const data = useOverview().data
  const worst = useLive((s) => worstTopic(s.topics))
  const attempt = worst?.attempt ?? 0
  const freshAt = useLive((s) => s.freshAt)
  const down = (data?.alerts ?? []).filter((a) => a.level === 'down')

  return (
    <div>
      {data?.claudeLimit.limited && (
        <div className="flex items-center gap-2 border-b border-warn/30 bg-warn/10 px-4 py-1.5 text-xs text-warn" role="status">
          <PauseCircle size={13} />
          Claude usage limit reached — all work paused{data.claudeLimit.resetsAt ? ` until ${clock(data.claudeLimit.resetsAt)}` : ''}, resumes automatically.
        </div>
      )}
      {down.map((a) => (
        <div key={a.id} className="flex items-center gap-2 border-b border-err/30 bg-err/10 px-4 py-1.5 text-xs text-err" role="alert">
          <AlertOctagon size={13} />
          <span className="text-fg">{a.what}</span>
          <span className="text-fg-subtle">— {a.todo}</span>
        </div>
      ))}
      {attempt > 1 && (
        <div className="flex items-center gap-2 border-b border-border bg-surface-2 px-4 py-1.5 text-xs text-fg-muted" role="status">
          <WifiOff size={13} />
          Live updates lost — reconnecting (attempt {attempt}).{freshAt ? ` Showing data from ${clock(freshAt)}.` : ''} Refreshing every minute meanwhile.
        </div>
      )}
    </div>
  )
}

export function Toasts() {
  const toasts = useLive((s) => s.toasts)
  const drop = useLive((s) => s.dropToast)
  useEffect(() => {
    if (!toasts.length) return
    const t = setTimeout(() => drop(toasts[0]!.id), 8_000)
    return () => clearTimeout(t)
  }, [toasts, drop])
  return (
    <div className="fixed right-4 bottom-4 z-50 flex flex-col gap-2" aria-live="polite">
      {toasts.map((t) => (
        <div key={t.id} className={`flex max-w-sm items-start gap-2 rounded-md border px-3 py-2 text-sm ${t.level === 'down' ? 'border-err/40 bg-surface-2 text-err' : 'border-border-strong bg-surface-2 text-fg'}`}>
          <span className="flex-1">{t.text}</span>
          <Button variant="ghost" size="icon-xs" onClick={() => drop(t.id)} aria-label="Dismiss" className="size-5 text-fg-subtle hover:bg-hover">
            <X size={12} />
          </Button>
        </div>
      ))}
    </div>
  )
}
